import { FC, useState, useRef, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetFooter, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Building2, MapPin, ClipboardCheck, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { StatusDisplay } from 'src/components/StatusDisplay';
import { Organization } from 'src/trpc';
import { OrderAcceptanceModes } from '@libs/schemas/src/organization';
// Import tab components
import {
  OrganizationOverviewTab,
  OrganizationOverviewTabRef,
} from 'src/components/OrganizationDrawer/OrganizationOverviewTab';
import { OrganizationCoverageTab } from 'src/components/OrganizationDrawer/OrganizationCoverageTab';
import { OrganizationUnassignedAreasTab } from 'src/components/OrganizationDrawer/OrganizationUnassignedAreasTab';

interface OrganizationDrawerProps {
  organization: Organization | null;
  isOpen: boolean;
  onClose: () => void;
}

type OrderAcceptanceMode = (typeof OrderAcceptanceModes)[keyof typeof OrderAcceptanceModes];

export const OrganizationDrawer: FC<OrganizationDrawerProps> = ({
  organization,
  isOpen,
  onClose,
}) => {
  const orderAcceptanceLabels: Record<OrderAcceptanceMode, string> = {
    [OrderAcceptanceModes.OPEN]: 'Orders Open',
    [OrderAcceptanceModes.ADMIN_ONLY]: 'Admin Only Orders',
    [OrderAcceptanceModes.CLOSED]: 'Orders Closed',
  };

  const orderAcceptanceClasses: Record<OrderAcceptanceMode, string> = {
    [OrderAcceptanceModes.OPEN]: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100',
    [OrderAcceptanceModes.ADMIN_ONLY]: 'bg-amber-100 text-amber-700 hover:bg-amber-100',
    [OrderAcceptanceModes.CLOSED]: 'bg-rose-100 text-rose-700 hover:bg-rose-100',
  };

  const [activeTab, setActiveTab] = useState('overview');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const overviewTabRef = useRef<OrganizationOverviewTabRef>(null);

  // Reset state when drawer opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('overview');
      setHasUnsavedChanges(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowConfirmDialog(true);
      return;
    }
    setHasUnsavedChanges(false);
    setActiveTab('overview');
    onClose();
  };

  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    setHasUnsavedChanges(false);
    setActiveTab('overview');

    onClose();
  };

  const handleCancelClose = () => {
    setShowConfirmDialog(false);
  };

  const handleSave = async () => {
    // Only save if we're on the overview tab
    if (activeTab === 'overview') {
      await overviewTabRef.current?.save();
    }
  };

  if (!organization) {
    return null;
  }

  const orderAcceptanceMode = organization.orderAcceptanceMode ?? OrderAcceptanceModes.OPEN;

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'Unknown';
    return format(new Date(date), "MMM do, yyyy 'at' h:mm a");
  };

  const confirmDialog = (
    <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Unsaved Changes</DialogTitle>
          <DialogDescription>
            You have unsaved changes that will be lost if you leave now. Are you sure you want to
            continue without saving?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancelClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirmClose} className="bg-red-600 hover:bg-red-700">
            Leave without saving
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      <Sheet
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleClose();
          }
        }}
      >
        <SheetContent
          side="right"
          className="p-0 sm:max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] h-full"
        >
          <div className="grid h-full grid-rows-[auto_auto_1fr_auto]">
            {/* Header */}
            <SheetHeader className="border-b bg-white px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="rounded-lg bg-orange-100 p-2">
                    <Building2 className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <SheetTitle className="text-xl font-semibold text-gray-900">
                      {organization.name}
                    </SheetTitle>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <span>ID: {organization._id}</span>
                      <StatusDisplay
                        status={organization.status === 'active' ? 'active' : 'suspended'}
                        displayTextOverride={
                          organization.status.charAt(0).toUpperCase() + organization.status.slice(1)
                        }
                      />
                      <Badge className={orderAcceptanceClasses[orderAcceptanceMode]}>
                        {orderAcceptanceLabels[orderAcceptanceMode]}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </SheetHeader>

            {/* Navigation Tabs */}
            <div className="border-b bg-gray-100 px-6 py-3">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-3 bg-gray-200 p-1 rounded-lg">
                  <TabsTrigger
                    value="overview"
                    className="flex items-center justify-center space-x-2 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md transition-all"
                  >
                    <Building2 className="h-4 w-4" />
                    <span>Overview</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="coverage"
                    className="flex items-center justify-center space-x-2 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md transition-all"
                  >
                    <MapPin className="h-4 w-4" />
                    <span>Coverage</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="unassigned-areas"
                    className="flex items-center justify-center space-x-2 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md transition-all"
                  >
                    <ClipboardCheck className="h-4 w-4" />
                    <span>Unassigned Areas</span>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Content Area - Scrollable */}
            <div className="overflow-y-auto">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsContent value="overview" className="mt-0 p-6">
                  <OrganizationOverviewTab
                    ref={overviewTabRef}
                    organization={organization}
                    onDataChange={setHasUnsavedChanges}
                    onSaveSuccess={() => {
                      setHasUnsavedChanges(false);
                      handleClose();
                    }}
                  />
                </TabsContent>

                <TabsContent value="coverage" className="mt-0 p-6">
                  <OrganizationCoverageTab
                    organizationId={organization._id}
                    onDataChange={setHasUnsavedChanges}
                  />
                </TabsContent>

                <TabsContent value="unassigned-areas" className="mt-0 p-6">
                  <OrganizationUnassignedAreasTab
                    organizationId={organization._id}
                    onDataChange={setHasUnsavedChanges}
                  />
                </TabsContent>
              </Tabs>
            </div>

            {/* Footer */}
            <SheetFooter className="border-t bg-gray-50 px-6 py-4">
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <Calendar className="h-4 w-4" />
                  <span>
                    Last updated:{' '}
                    {organization.updatedAt ? formatDate(organization.updatedAt) : 'Unknown'}
                  </span>
                </div>
                <div className="flex space-x-2">
                  <Button variant="outline" onClick={handleClose}>
                    Cancel
                  </Button>
                  {activeTab === 'overview' && (
                    <Button
                      onClick={handleSave}
                      disabled={!hasUnsavedChanges}
                      className="bg-orange-600 hover:bg-orange-700"
                    >
                      Save Changes
                    </Button>
                  )}
                </div>
              </div>
            </SheetFooter>
          </div>
        </SheetContent>
      </Sheet>

      {confirmDialog}
    </>
  );
};
